import { useState } from "react";
import type { AuditEvent } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { formatAuditValue, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { groupConsecutive, groupValueRange, type AuditGroup } from "./groupConsecutive";
import { presentationFor } from "./registry";

const TONE_CLASSES = {
  neutral: "text-muted-foreground",
  positive: "text-success",
  negative: "text-destructive",
  warning: "text-warning",
} as const;

/**
 * What a row may render after the verb to name the record it happened to.
 *
 * A per-record timeline passes nothing: every row on it is about the record already on
 * screen, and repeating its name twenty times would be noise. A **cross-record** feed — the
 * activity view — passes a link, because a row that cannot be opened is a dead end. One
 * presentation either way, which is the point: the feed and the record can never tell
 * different stories about the same event, because they render through the same component.
 */
export type RecordSlot = (event: AuditEvent) => React.ReactNode;

/**
 * The history section of a detail screen, and the company-wide feed.
 *
 * Every change is its own row in the database; consecutive similar rows collapse here
 * into one expandable line. That is how the presentation stays readable without the log
 * losing anything — see `groupConsecutive`.
 */
export function AuditTimeline({
  events,
  total,
  onShowAll,
  showingAll,
  renderRecord,
}: {
  events: AuditEvent[];
  total?: number;
  onShowAll?: () => void;
  showingAll?: boolean;
  renderRecord?: RecordSlot;
}) {
  const t = useT();
  const groups = groupConsecutive(events);

  if (events.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("audit.noHistory")}</p>;
  }

  return (
    <div className="space-y-1">
      <ol className="space-y-1">
        {groups.map((group) => (
          <GroupRow key={group.key} group={group} renderRecord={renderRecord} />
        ))}
      </ol>

      {/* The book's recommendation: ten most recent, then everything. */}
      {onShowAll && !showingAll && total !== undefined && total > events.length && (
        <Button variant="link" size="sm" onClick={onShowAll} className="px-0">
          {t("audit.showAll", total)}
        </Button>
      )}
    </div>
  );
}

function GroupRow({ group, renderRecord }: { group: AuditGroup; renderRecord?: RecordSlot }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const newest = group.events[0]!;
  const presentation = presentationFor(newest.eventType, newest);
  const Icon = presentation.icon;
  const count = group.events.length;
  const range = groupValueRange(group);

  return (
    <li className="border-border/60 flex gap-3 border-b py-2 last:border-b-0">
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", TONE_CLASSES[presentation.tone ?? "neutral"])} aria-hidden />

      <div className="min-w-0 flex-1">
        <p className="text-sm">
          {/* The separator is a catalogue entry: English reads straight on from the name
              ("Hana changed the name"), Czech nominalises and needs a dash, because a Czech
              verb would have to agree with a gender no audit row records. */}
          <span className="font-medium">{newest.actorName}</span>
          {t("audit.actorSeparator")}
          {t(presentation.labelKey)}
          {/* The record, after the verb — every group's events share one, because the scope
              is part of the grouping signature. */}
          {renderRecord && <>{" "}{renderRecord(newest)}</>}
          {count > 1 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              className="bg-secondary text-secondary-foreground hover:bg-accent ml-2 rounded-full px-2 py-0.5 text-xs"
            >
              {t("audit.repeated", count)}
            </button>
          )}
        </p>

        {presentation.showValues && (range.oldValue || range.newValue) && (
          <p className="text-muted-foreground mt-0.5 text-xs">
            {range.oldValue ? <span className="line-through">{formatAuditValue(range.oldValue)}</span> : <span>—</span>}
            <span aria-hidden> → </span>
            <span className="text-foreground">{formatAuditValue(range.newValue) ?? "—"}</span>
          </p>
        )}

        {/* Expanded, each underlying row is shown — nothing was merged away. */}
        {expanded && count > 1 && (
          <ul className="border-border/60 mt-1.5 space-y-1 border-l pl-3">
            {group.events.map((event) => (
              <li key={event.id} className="text-muted-foreground text-xs">
                <time dateTime={event.occurredAt}>{formatDateTime(event.occurredAt)}</time>
                {presentation.showValues && (event.oldValue || event.newValue) && (
                  <>
                    {" — "}
                    <span className="line-through">{formatAuditValue(event.oldValue) ?? "—"}</span>
                    <span aria-hidden> → </span>
                    <span className="text-foreground">{formatAuditValue(event.newValue) ?? "—"}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <time dateTime={newest.occurredAt} className="text-muted-foreground shrink-0 text-xs">
        {formatDateTime(newest.occurredAt)}
      </time>
    </li>
  );
}
